// hooks/useBookmarks.ts — reactive hook for bookmarks.
//
// Reads from bookmarkStore reactively and exposes service mutations.
// Components must NOT import bookmarkStore or bookmarksService directly.
//
// useShallow is used on any filtering/derived-list selector to prevent
// re-renders when unrelated bookmarks change.

import { useShallow } from 'zustand/react/shallow';
import { useBookmarkStore } from '@/core/stores/bookmarkStore';
import { bookmarksService } from '@/core/services/bookmarksService';
import type { Bookmark, BookmarkFolder, BookmarkType } from '@/core/models/bookmark';
import type { Post } from '@/core/models/post';

export interface BookmarksHook {
  /** All bookmarks sorted by savedAt descending. */
  bookmarks: Bookmark[];
  /** All bookmark folders. */
  folders: BookmarkFolder[];
  /** Total number of bookmarks. */
  totalCount: number;
  /** Count of bookmarks saved this week. */
  thisWeekCount: number;
  /** Remove (un-save) a bookmark by id. */
  remove: (id: string) => Promise<void>;
  /** Create a new folder with the given name. */
  createFolder: (name: string) => Promise<BookmarkFolder>;
  /** Rename an existing folder. */
  renameFolder: (id: string, name: string) => Promise<void>;
  /** Delete a folder by id. */
  deleteFolder: (id: string) => Promise<void>;
  /** Move a bookmark into a folder. */
  moveBookmark: (bookmarkId: string, folderId: string) => Promise<void>;
}

export interface BookmarksByTypeHook {
  /** Bookmarks filtered to a specific content type. */
  bookmarks: Bookmark[];
}

export interface BookmarksByFolderHook {
  /** Bookmarks filtered to a specific folder. */
  bookmarks: Bookmark[];
}

/** Primary hook: all bookmarks + mutations. */
export function useBookmarks(): BookmarksHook {
  // Sorting is a derivation — returns a new array ref each eval, so useShallow
  // is required to avoid spurious re-renders / useSyncExternalStore warnings.
  const bookmarks = useBookmarkStore(
    useShallow((s) => [...s.bookmarks].sort((a, b) => b.savedAt - a.savedAt)),
  );
  const folders = useBookmarkStore((s) => s.folders);
  const totalCount = bookmarks.length;
  const thisWeekCount = bookmarks.filter((b) => b.recent).length;

  return {
    bookmarks,
    folders,
    totalCount,
    thisWeekCount,
    remove: (id) => bookmarksService.remove(id),
    createFolder: (name) => bookmarksService.createFolder(name),
    renameFolder: (id, name) => bookmarksService.renameFolder(id, name),
    deleteFolder: (id) => bookmarksService.deleteFolder(id),
    moveBookmark: (bookmarkId, folderId) => bookmarksService.moveBookmark(bookmarkId, folderId),
  };
}

/**
 * Filtered hook: bookmarks for a specific content type.
 * Uses useShallow because the selector returns a filtered array.
 */
export function useBookmarksByType(type: BookmarkType): BookmarksByTypeHook {
  const bookmarks = useBookmarkStore(useShallow((s) => s.bookmarks.filter((b) => b.type === type)));
  return { bookmarks };
}

/**
 * Filtered hook: bookmarks for a specific folder.
 * Uses useShallow because the selector returns a filtered array.
 */
export function useBookmarksByFolder(folderId: string): BookmarksByFolderHook {
  const bookmarks = useBookmarkStore(
    useShallow((s) => s.bookmarks.filter((b) => b.folderId === folderId)),
  );
  return { bookmarks };
}

export interface PostBookmarkHook {
  isBookmarked: boolean;
  toggle: () => Promise<boolean>;
}

/**
 * Reactive bookmark state for a single post.
 * Scalar boolean selector — no useShallow.
 */
export function usePostBookmark(post: Post): PostBookmarkHook {
  const isBookmarked = useBookmarkStore((s) =>
    s.bookmarks.some((b) => b.postId === post.id && !b.commentId),
  );
  return {
    isBookmarked,
    toggle: () => bookmarksService.toggle(post),
  };
}
